import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { Session } from './client'

/**
 * Die angemeldete Sitzung, für alle Ansichten erreichbar.
 *
 * Der Wächter lädt sie ohnehin, um zu prüfen, ob die Zugangsdaten noch gelten.
 * Sie danach wegzuwerfen und in jeder Ansicht neu zu holen wäre ein Aufruf je
 * Seitenwechsel für eine Antwort, die sich innerhalb einer Sitzung nicht ändert.
 *
 * `null` bedeutet: Es ist nicht bekannt, wer angemeldet ist — der Dienst war
 * beim Prüfen nicht erreichbar. Dann kann die Oberfläche keine Rechte vorab
 * prüfen und muss es dem Server überlassen.
 */
const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ session, children }: {
  session: Session | null
  children: ReactNode
}) {
  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): Session | null {
  return useContext(SessionContext)
}

/**
 * Hat der Angemeldete dieses Recht?
 *
 * Ist die Sitzung unbekannt, lautet die Antwort `true`. Das ist gewollt: Die
 * Prüfung hier ist Bequemlichkeit — sie erspart einen Aufruf, der ins 403 läuft.
 * Verbindlich geprüft wird am Server, bei jedem Aufruf. Bei `false` sähe der
 * Angemeldete eine Rechte-Meldung, obwohl das Recht vielleicht besteht und nur
 * der Dienst kurz nicht erreichbar war.
 */
export function useHasPermission(permission: string): boolean {
  const session = useSession()
  return session === null || session.permissions.includes(permission)
}
